import round from 'lodash/round'
import i18n from '@/i18n'

/**
 * Formats a time duration into a string representation.
 *
 * @param {number} time - The time in seconds to be formatted
 * @param {boolean} [realTime=false] - If true, includes leading zeros for days and hours
 *                                    even when they are 0. If false, omits zero values
 * @returns {string} A formatted time string in the format "DDd:HH:MM:SS" where:
 *                  - DD (days) and HH (hours) are only shown if non-zero or if realTime is true
 *                  - All numbers except days are padded with leading zeros if less than 10
 * @example
 * timeFormatter(3661)      // Returns "01:01:01"
 * timeFormatter(3661, true) // Returns "00:01:01:01"
 */
export function timeFormatter (time, realTime = false) {
    const days = Math.floor(time / 86400)
    time -= days * 86400

    const hours = Math.floor(time / 3600) % 24
    time -= hours * 3600

    const minutes = Math.floor(time / 60) % 60
    time -= minutes * 60

    const seconds = Math.floor(time % 60)

    const data = {
        days: days ? `${days}d:` : realTime ? '00:' : '',
        hours: hours ? `${addPrefix(hours)}:` : realTime ? '00:' : '',
        minutes: `${addPrefix(minutes)}:`,
        seconds: addPrefix(seconds)
    }

    return Object.values(data).join('')
}

/**
 * Formats a duration of time into a human-readable string.
 *
 * @param {number} time - The time in seconds to be formatted
 * @returns {string} A formatted string in the format "Xh Ym Zs" where:
 *                  - X represents hours (only shown if non-zero)
 *                  - Y represents minutes (only shown if non-zero)
 *                  - Z represents seconds (always shown)
 *                  Each unit is followed by its identifier (h/m/s)
 * @example
 * formatDuration(3661)  // Returns "1h 1m 1s"
 * formatDuration(61)    // Returns "1m 1s"
 * formatDuration(10)    // Returns "10s"
 */
export function formatDuration (time) {
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time % 3600) / 60)
    const seconds = round(time % 60)

    let formattedTime = ''
    if (hours > 0) formattedTime += `${hours}${i18n.global.t('general.time.hoursShort')} `
    if (minutes > 0) formattedTime += `${minutes}${i18n.global.t('general.time.minutesShort')} `
    formattedTime += `${seconds}${i18n.global.t('general.time.secondsShort')}`

    return formattedTime.trim()
}

function addPrefix (value) {
    if (value < 10) {
        return `0${value}`
    }
    return value
}
